import React from 'react';
import { Link } from 'react-router-dom';
import { Apple, Leaf, Heart, BookMarked, GlassWater, UtensilsCrossed, ShieldCheck } from 'lucide-react';

/**
 * Preview Integrative Health Page
 * Matches the live IntegrativeHealthPage layout exactly
 * Read-only with disabled card interactions (except linked preview pages)
 */
const PreviewIntegrativeHealthPage = () => {
  const subsections = [
    {
      title: 'Food & Nutrition',
      description: 'Educational information about whole foods, dietary patterns, and nutritional principles commonly discussed in integrative oncology and patient communities.',
      icon: Apple
    },
    {
      title: 'Recipes (Supportive)',
      description: 'Simple, whole-food recipes often chosen by people seeking plant-forward, anti-inflammatory eating patterns.',
      icon: UtensilsCrossed,
      previewPath: '/preview/recipes'
    },
    {
      title: 'Smoothies',
      description: 'Educational information about smoothies for nutritional support when appetite is low or eating feels challenging.',
      icon: GlassWater,
      previewPath: '/preview/smoothies'
    },
    {
      title: 'Eating Well With CLL',
      description: 'Supportive nutrition guidance covering practical approaches like eating enough, food variety, and fatigue-friendly eating.',
      icon: Apple,
      previewPath: '/preview/eating-well'
    },
    {
      title: 'Food Safety',
      description: 'Important food safety information including raw food risks, temperature safety, and immunocompromised considerations.',
      icon: ShieldCheck,
      previewPath: '/preview/food-safety'
    },
    {
      title: 'Supplements (Educational)',
      description: 'Non-prescriptive information about vitamins, minerals, and supplements that are sometimes discussed in CLL communities.',
      icon: Leaf
    },
    {
      title: 'Lifestyle & Habits',
      description: 'Movement, sleep, stress management, and daily habits that may support general wellbeing.',
      icon: Heart
    },
    {
      title: 'Learn & Notes',
      description: 'A personal space to record learnings, reflections, and questions as you explore Integrative Health content.',
      icon: BookMarked
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
          Integrative Health Support for People Living with CLL
        </h1>
        
        {/* Introduction Section */}
        <div className="bg-gradient-to-br from-teal-50 to-blue-50 border border-teal-200 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            How to use this section
          </h2>
          
          <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
            <p>
              Many people living with CLL explore topics like nutrition, lifestyle, and supplements alongside their standard medical care. This section provides educational context for those conversations — not plans, protocols, or recommendations.
            </p>
            
            <p>
              The information here is intended to help you understand what these topics mean, why they&apos;re sometimes discussed, and what questions you might want to bring to your healthcare team. It&apos;s not medical advice, and it doesn&apos;t replace clinical guidance.
            </p>
            
            <p>
              Some people find this content helpful when thinking about their overall wellbeing. Others prefer to focus only on clinical care. Both approaches are valid. Use what feels relevant to you, and skip what doesn&apos;t.
            </p>
            
            <p className="font-medium text-slate-900 pt-2 border-t border-slate-200">
              If something here interests you, we encourage you to discuss it with your doctor, oncologist, or dietitian. They can help you understand what might or might not be appropriate for your specific situation.
            </p>
          </div>
        </div>
      </div>

      {/* Subsections Grid - Read-only cards (some with preview links) */}
      <div className="grid md:grid-cols-2 gap-6">
        {subsections.map((section) => {
          const Icon = section.icon;
          
          // If section has a preview path, make it a clickable link
          if (section.previewPath) {
            return (
              <Link
                key={section.title}
                to={section.previewPath}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                      {section.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {section.description}
                    </p>
                    <p className="text-xs text-teal-600 mt-2">View preview →</p>
                  </div>
                </div>
              </Link>
            );
          }
          
          return (
            <div
              key={section.title}
              className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm opacity-75 cursor-not-allowed"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-50 rounded-xl">
                  <Icon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {section.description}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">Available in full app</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PreviewIntegrativeHealthPage;
